/** On-page pins for Magic-Link reviewer history (KA-747). */

export type PinItem = {
  index: number
  selector: string
  pageUrl: string
  status?: 'open' | 'done' | 'discarded'
}

function pageKey(url: string): string {
  try {
    const u = new URL(url)
    return `${u.origin}${u.pathname}`
  } catch {
    return url.split('?')[0]?.split('#')[0] ?? url
  }
}

function samePage(a: string, b: string): boolean {
  return pageKey(a) === pageKey(b)
}

export type PinLayer = {
  render: (items: PinItem[]) => void
  focus: (selector: string) => boolean
  destroy: () => void
}

export function mountPinLayer(): PinLayer {
  const root = document.createElement('div')
  root.setAttribute('data-klfb-pins', '1')
  root.style.cssText =
    'position:absolute;left:0;top:0;width:0;height:0;overflow:visible;z-index:2147483644;pointer-events:none;'
  document.documentElement.appendChild(root)

  const style = document.createElement('style')
  style.textContent = `
    [data-klfb-pin] {
      position: absolute;
      width: 22px; height: 22px; border-radius: 999px;
      background: #6D28D9; color: #fff;
      font: 700 10px/22px ui-sans-serif, system-ui, sans-serif;
      text-align: center;
      box-shadow: 0 4px 12px rgba(109,40,217,.45);
      transform: translate(50%, -50%);
      z-index: 2147483644;
      pointer-events: none;
    }
    [data-klfb-pin][data-status="done"] {
      background: #15803d;
      box-shadow: 0 4px 12px rgba(21,128,61,.35);
    }
    [data-klfb-pin][data-status="discarded"] {
      background: transparent; color: #78716c;
      border: 2px solid #a8a29e;
      box-shadow: none;
      line-height: 18px;
    }
    [data-klfb-pin][data-focus="1"] {
      box-shadow: 0 0 0 3px #fff, 0 0 0 6px #6D28D9, 0 4px 12px rgba(109,40,217,.45);
      animation: klfbPinPulse 1.4s ease-in-out infinite;
    }
    [data-klfb-focus-ring] {
      outline: 3px solid #6D28D9 !important;
      outline-offset: 2px !important;
      background-color: rgba(237, 233, 254, 0.45) !important;
    }
    @media (prefers-reduced-motion: reduce) {
      [data-klfb-pin][data-focus="1"] { animation: none !important; }
    }
    @keyframes klfbPinPulse {
      0%, 100% { transform: translate(50%, -50%) scale(1); }
      50% { transform: translate(50%, -50%) scale(1.08); }
    }
  `
  document.head.appendChild(style)

  let lastFocusEl: Element | null = null
  let placed: { el: HTMLElement; sel: string }[] = []

  const clearFocus = () => {
    if (lastFocusEl) {
      lastFocusEl.removeAttribute('data-klfb-focus-ring')
      lastFocusEl = null
    }
    for (const p of placed) p.el.removeAttribute('data-focus')
  }

  const placePins = (items: PinItem[]) => {
    root.replaceChildren()
    placed = []
    const here = location.href
    const onPage = items.filter((it) => it.selector && samePage(it.pageUrl || here, here))
    for (const it of onPage) {
      let target: Element | null = null
      try {
        target = document.querySelector(it.selector)
      } catch {
        target = null
      }
      if (!target) continue
      const rect = target.getBoundingClientRect()
      const pin = document.createElement('div')
      pin.setAttribute('data-klfb-pin', '1')
      const st = it.status ?? 'open'
      pin.setAttribute('data-status', st)
      const statusDe = st === 'done' ? 'erledigt' : st === 'discarded' ? 'verworfen' : 'offen'
      pin.setAttribute('aria-label', `Feedback ${it.index}, ${statusDe}`)
      pin.removeAttribute('aria-hidden')
      pin.textContent = String(it.index)
      const top = rect.top + window.scrollY
      const left = rect.right + window.scrollX
      pin.style.top = `${top}px`
      pin.style.left = `${left}px`
      root.appendChild(pin)
      placed.push({ el: pin, sel: it.selector })
    }
  }

  const onScrollOrResize = () => {
    placePins(lastItems)
  }
  window.addEventListener('scroll', onScrollOrResize, true)
  window.addEventListener('resize', onScrollOrResize)

  let lastItems: PinItem[] = []
  let focusedSel: string | null = null

  return {
    render(items) {
      lastItems = items
      placePins(items)
      if (focusedSel) {
        for (const p of placed) {
          if (p.sel === focusedSel) p.el.setAttribute('data-focus', '1')
        }
      }
    },
    focus(selector) {
      clearFocus()
      focusedSel = selector
      let target: Element | null = null
      try {
        target = document.querySelector(selector)
      } catch {
        return false
      }
      if (!target) return false
      target.setAttribute('data-klfb-focus-ring', '1')
      lastFocusEl = target
      target.scrollIntoView({ behavior: 'smooth', block: 'center' })
      window.setTimeout(() => {
        placePins(lastItems)
        for (const p of placed) {
          if (p.sel === selector) p.el.setAttribute('data-focus', '1')
        }
      }, 350)
      return true
    },
    destroy() {
      focusedSel = null
      clearFocus()
      window.removeEventListener('scroll', onScrollOrResize, true)
      window.removeEventListener('resize', onScrollOrResize)
      root.remove()
      style.remove()
    },
  }
}

export function samePageUrl(a: string, b: string): boolean {
  return samePage(a, b)
}
